import type { BoxProps } from "@qws-org/ui-kit";
import { Flex } from "@qws-org/ui-kit";
import type { FC } from "react";

import type { Action, MainBannerV4 as MainBannerV4Type, Pages } from "~/core";
import { CmsImage } from "~/shared/ui/CmsImage";
import type { ImageParams } from "~/shared/utils/directus/useDirectus";
import { getPictureData } from "~/shared/utils/get-picture-data";

import { MainBannerV4Buttons } from "./components/MainBannerV4Buttons";
import { TextContent } from "./components/TextContent";

export type MainBannerV4Props = {
  data: MainBannerV4Type;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  getImageUrl: (imageId: string, params?: ImageParams) => string;
  isControlInactive: (control: Action) => boolean;
  /** True when this block is rendered first on the page. */
  isFirstOnPage?: boolean;
} & BoxProps;

export const MainBannerV4: FC<MainBannerV4Props> = (props) => {
  const { data, isFirstOnPage, ...rest } = props;

  const bg = getPictureData(data?.background);
  const hasImage = Boolean(bg.id);

  return (
    <Flex
      position={hasImage ? { type: "relative", zIndex: 0 } : undefined}
      width="100%"
      justify={"center"}
      height={bg.mHeight}
      bg={hasImage ? undefined : bg.mColor}
      {...rest}
      md={{
        justify: "normal",
        bg: hasImage ? undefined : bg.mdColor,
        maxWidth: 1024,
        height: bg.mdHeight,

        ...rest.md,
      }}
      d={{
        bg: hasImage ? undefined : bg.dColor,

        justify: "center",
        maxWidth: 1920,
        height: bg.dHeight,
        ...rest.d,
      }}
    >
      {hasImage && (
        <CmsImage
          id={bg.id}
          tabletId={bg.mdId}
          desktopId={bg.dId}
          width={bg.dWidth ?? bg.mdWidth ?? bg.mWidth ?? 1920}
          height={bg.dHeight ?? bg.mdHeight ?? bg.mHeight ?? 720}
          alt={bg.altText}
          priority={isFirstOnPage ?? false}
          className="absolute inset-0 -z-10 [&_img]:w-full [&_img]:h-full [&_img]:object-cover"
        />
      )}
      <Flex maxWidth={1240} width={"100%"} justify={"center"}>
        <Flex
          flexDirection="column"
          width={"100%"}
          justify={"center"}
          md={{ gap: 64 }}
          gap={0}
          m={{ mt: 0 }}
          p={{ px: 16 }}
        >
          <TextContent
            title={data?.title}
            description={data?.description}
            items={data?.list_items}
          />

          {data?.controls && <MainBannerV4Buttons {...props} />}
        </Flex>
      </Flex>
    </Flex>
  );
};
